import { combineReducers } from 'redux'
import get from 'lodash/get'
import union from 'lodash/union'
import createPaginator from './create-paginator'

import {
  LOCATION_AREAS_REQUEST,
  LOCATION_AREAS_SUCCESS,
  LOCATION_AREAS_FAILURE
} from '../actions/areas'

import {
  AREA_ACTIVITIES_REQUEST,
  AREA_ACTIVITIES_SUCCESS,
  AREA_ACTIVITIES_FAILURE
} from '../actions/area-activities'

import {
  INSPECTORS_REQUEST,
  INSPECTORS_SUCCESS,
  INSPECTORS_FAILURE,
} from '../actions/inspectors'

import {
  LOCATION_INSPECTIONS_REQUEST,
  LOCATION_INSPECTIONS_SUCCESS,
  LOCATION_INSPECTIONS_FAILURE,
} from '../actions/inspections'

import {
  LOCATION_ISSUES_REQUEST,
  LOCATION_ISSUES_SUCCESS,
  LOCATION_ISSUES_FAILURE,
  SERVICING_ISSUES_REQUEST,
  SERVICING_ISSUES_SUCCESS,
  SERVICING_ISSUES_FAILURE,
  AREA_ISSUES_REQUEST,
  AREA_ISSUES_SUCCESS,
  AREA_ISSUES_FAILURE,
  DELETE_ISSUE_SUCCESS
} from '../actions/issues'

import {
  LOCATION_AREA_TYPES_REQUEST,
  LOCATION_AREA_TYPES_SUCCESS,
  LOCATION_AREA_TYPES_FAILURE
} from '../actions/area-types'

import {
  LOCATION_SUB_LOCATIONS_REQUEST,
  LOCATION_SUB_LOCATIONS_SUCCESS,
  LOCATION_SUB_LOCATIONS_FAILURE
} from '../actions/sub-locations'

import {
  LOCATIONS_REQUEST,
  LOCATIONS_SUCCESS,
  LOCATIONS_FAILURE
} from '../actions/locations'

import {
  PIN_HISTORIES_REQUEST,
  PIN_HISTORIES_SUCCESS,
  PIN_HISTORIES_FAILURE
} from '../actions/histories'

import {
  FETCH_ISSUE_HISTORY_REQUEST,
  FETCH_ISSUE_HISTORY_SUCCESS,
  FETCH_ISSUE_HISTORY_FAILURE
} from '../actions/issue-histories'

import {
  FETCH_WORK_ORDER_COMMENTS_REQUEST,
  FETCH_WORK_ORDER_COMMENTS_SUCCESS,
  FETCH_WORK_ORDER_COMMENTS_FAILURE,
  CREATE_WORK_ORDER_COMMENT_SUCCESS,
  FETCH_ISSUE_COMMENTS_REQUEST,
  FETCH_ISSUE_COMMENTS_SUCCESS,
  FETCH_ISSUE_COMMENTS_FAILURE,
  CREATE_ISSUE_COMMENT_SUCCESS
} from '../actions/comments'

import {
  QA_WORK_ORDERS_REQUEST,
  QA_WORK_ORDERS_SUCCESS,
  QA_WORK_ORDERS_FAILURE,
  CREATE_QA_WORK_ORDER_SUCCESS,
  DELETE_QA_WORK_ORDER_SUCCESS
} from '../actions/qa-work-orders'

import {
  QA_SERVICINGS_REQUEST,
  QA_SERVICINGS_SUCCESS,
  QA_SERVICINGS_FAILURE,
  CREATE_QA_SERVICING_SUCCESS,
  DELETE_QA_SERVICING_SUCCESS
} from '../actions/qa-servicings'

import {
  QA_TASKS_REQUEST,
  QA_TASKS_SUCCESS,
  QA_TASKS_FAILURE
} from '../actions/qa-tasks'

import {
  SERVICINGS_REQUEST,
  SERVICINGS_SUCCESS,
  SERVICINGS_FAILURE,
  CREATE_SERVICING_SUCCESS,
  DELETE_SERVICING_SUCCESS
} from '../actions/servicings'

import {
  REPORTS_REQUEST,
  REPORTS_SUCCESS,
  REPORTS_FAILURE
} from '../actions/reports'

import {
  WORK_ORDERS_REQUEST,
  WORK_ORDERS_SUCCESS,
  WORK_ORDERS_FAILURE,
  LOCATION_WORK_ORDERS_REQUEST,
  LOCATION_WORK_ORDERS_SUCCESS,
  LOCATION_WORK_ORDERS_FAILURE,
  VALID_WORK_ORDERS_REQUEST,
  VALID_WORK_ORDERS_SUCCESS,
  VALID_WORK_ORDERS_FAILURE,
  CREATE_WORK_ORDER_SUCCESS,
  DELETE_WORK_ORDER_SUCCESS,
  LIVE_LOAD_WORK_ORDER
} from '../actions/work-orders'

import {
  WORK_SUMMARIES_REQUEST,
  WORK_SUMMARIES_SUCCESS,
  WORK_SUMMARIES_FAILURE
} from '../actions/work-summaries'

export default combineReducers({
  areasByLocation: createPaginator({
    types: [ LOCATION_AREAS_REQUEST, LOCATION_AREAS_SUCCESS, LOCATION_AREAS_FAILURE ],
    mapActionToKey: action => {
      if (!action.payload) {
        return
      }

      return action.payload.paginationKey
    }
  }),

  activitiesByArea: createPaginator({
    types: [ AREA_ACTIVITIES_REQUEST, AREA_ACTIVITIES_SUCCESS, AREA_ACTIVITIES_FAILURE ],
    mapActionToKey: action => action.payload && action.payload.areaId.toString(),
    idCombiner: (action, existing, incoming) => {
      if (action.payload.isNewPage) {
        return union(existing, incoming)
      } else {
        return union(incoming, existing)
      }
    }
  }),

  areaTypesByLocation: createPaginator({
    types: [ LOCATION_AREA_TYPES_REQUEST, LOCATION_AREA_TYPES_SUCCESS, LOCATION_AREA_TYPES_FAILURE ],
    mapActionToKey: action => action.payload && action.payload.locationId.toString()
  }),

  subLocationsByLocation: createPaginator({
    types: [ LOCATION_SUB_LOCATIONS_REQUEST, LOCATION_SUB_LOCATIONS_SUCCESS, LOCATION_SUB_LOCATIONS_FAILURE ],
    mapActionToKey: action => action.payload && action.payload.locationId.toString()
  }),

  commentsByWorkOrder: createPaginator({
    types: [ FETCH_WORK_ORDER_COMMENTS_REQUEST, FETCH_WORK_ORDER_COMMENTS_SUCCESS, FETCH_WORK_ORDER_COMMENTS_FAILURE ],
    createType: CREATE_WORK_ORDER_COMMENT_SUCCESS,
    mapActionToKey: action => action.payload && action.payload.workOrderId.toString(),
    prependCreate: true
  }),

  commentsByIssue: createPaginator({
    types: [ FETCH_ISSUE_COMMENTS_REQUEST, FETCH_ISSUE_COMMENTS_SUCCESS, FETCH_ISSUE_COMMENTS_FAILURE ],
    createType: CREATE_ISSUE_COMMENT_SUCCESS,
    mapActionToKey: action => action.payload && action.payload.issueId.toString(),
    prependCreate: true
  }),

  historiesByPin: createPaginator({
    types: [ PIN_HISTORIES_REQUEST, PIN_HISTORIES_SUCCESS, PIN_HISTORIES_FAILURE ],
    mapActionToKey: action => action.payload && action.payload.pinId.toString()
  }),

  historiesByIssue: createPaginator({
    types: [ FETCH_ISSUE_HISTORY_REQUEST, FETCH_ISSUE_HISTORY_SUCCESS, FETCH_ISSUE_HISTORY_FAILURE ],
    mapActionToKey: action => action.payload && action.payload.issueId.toString()
  }),

  issuesByLocation: createPaginator({
    types: [ LOCATION_ISSUES_REQUEST, LOCATION_ISSUES_SUCCESS, LOCATION_ISSUES_FAILURE ],
    deleteType: DELETE_ISSUE_SUCCESS,
    mapActionToKey: action => generateResourceId(action, 'locationId')
  }),

  validWorkOrders: createPaginator({
    types: [ VALID_WORK_ORDERS_REQUEST, VALID_WORK_ORDERS_SUCCESS, VALID_WORK_ORDERS_FAILURE ],
    mapActionToKey: action => action.payload && action.payload.areaId
  }),

  inspectionsByLocation: createPaginator({
    types: [ LOCATION_INSPECTIONS_REQUEST, LOCATION_INSPECTIONS_SUCCESS, LOCATION_INSPECTIONS_FAILURE ],
    mapActionToKey: action => generateResourceId(action, 'locationId')
  }),

  inspectorsByLocation: createPaginator({
    types: [ INSPECTORS_REQUEST, INSPECTORS_SUCCESS, INSPECTORS_FAILURE ],
    mapActionToKey: action => generateResourceId(action, 'locationId')
  }),

  issuesByServicing: createPaginator({
    types: [ SERVICING_ISSUES_REQUEST, SERVICING_ISSUES_SUCCESS, SERVICING_ISSUES_FAILURE ],
    deleteType: DELETE_ISSUE_SUCCESS,
    mapActionToKey: action => generateResourceId(action, 'servicingId')
  }),

  issuesByArea: createPaginator({
    types: [ AREA_ISSUES_REQUEST, AREA_ISSUES_SUCCESS, AREA_ISSUES_FAILURE ],
    deleteType: DELETE_ISSUE_SUCCESS,
    mapActionToKey: action => generateResourceId(action, 'areaId')
  }),

  workOrdersByLocation: createPaginator({
    types: [ LOCATION_WORK_ORDERS_REQUEST, LOCATION_WORK_ORDERS_SUCCESS, LOCATION_WORK_ORDERS_FAILURE ],
    deleteType: DELETE_WORK_ORDER_SUCCESS,
    mapActionToKey: (action) => generateResourceId(action, 'locationId')
  }),

  locations: createPaginator({
    types: [ LOCATIONS_REQUEST, LOCATIONS_SUCCESS, LOCATIONS_FAILURE ]
  }),

  qaServicingsByQAWorkOrder: createPaginator({
    types: [ QA_SERVICINGS_REQUEST, QA_SERVICINGS_SUCCESS, QA_SERVICINGS_FAILURE ],
    createType: CREATE_QA_SERVICING_SUCCESS,
    deleteType: DELETE_QA_SERVICING_SUCCESS,
    mapActionToKey: action => get(action, `payload.qaWorkOrderId`, 0).toString()
  }),

  qaTasksByQaServicing: createPaginator({
    types: [ QA_TASKS_REQUEST, QA_TASKS_SUCCESS, QA_TASKS_FAILURE ],
    mapActionToKey: action => action.payload && action.payload.qaServicingId.toString()
  }),

  qaWorkOrders: createPaginator({
    types: [ QA_WORK_ORDERS_REQUEST, QA_WORK_ORDERS_SUCCESS, QA_WORK_ORDERS_FAILURE ],
    createType: CREATE_QA_WORK_ORDER_SUCCESS,
    deleteType: DELETE_QA_WORK_ORDER_SUCCESS
  }),

  reports: createPaginator({
    types: [REPORTS_REQUEST, REPORTS_SUCCESS, REPORTS_FAILURE],
  }),

  servicingsByWorkOrder: createPaginator({
    types: [ SERVICINGS_REQUEST, SERVICINGS_SUCCESS, SERVICINGS_FAILURE ],
    createType: CREATE_SERVICING_SUCCESS,
    deleteType: DELETE_SERVICING_SUCCESS,
    mapActionToKey: (action) => (
      [
        get(action, 'payload.workOrderId', 0),
        get(action, 'payload.type', 0)
      ].join('-')
    )
  }),

  workOrders: createPaginator({
    types: [ WORK_ORDERS_REQUEST, WORK_ORDERS_SUCCESS, WORK_ORDERS_FAILURE ],
    createType: CREATE_WORK_ORDER_SUCCESS,
    deleteType: DELETE_WORK_ORDER_SUCCESS,
    insertType: LIVE_LOAD_WORK_ORDER
  }),

  workSummaries: createPaginator({
    types: [ WORK_SUMMARIES_REQUEST, WORK_SUMMARIES_SUCCESS, WORK_SUMMARIES_FAILURE ]
  }),
})

const generateResourceId = (action, key) => {
  const resourceId = get(action, `payload.${key}`) || get(action, 'payload.resourceId')
  return resourceId ? resourceId.toString() : ''
}



// WEBPACK FOOTER //
// ./src/reducers/pagination.js